import React, { useEffect, useMemo, useState } from 'react';
import {
  Badge,
  Button,
  ChevronDownIcon,
  ChevronRightIcon,
  CogIcon,
  DocumentTextIcon,
  EyeIcon,
  GripVerticalIcon,
  MapPinIcon,
  Switch,
  ViewColumnsIcon,
} from '../../components/ui';
import { Modal, ModalContent, ModalFooter, ModalHeader, ModalTitle } from '../../components/ui/Modal';
import {
  collectSidebarPathLabels,
  getSidebarItems,
} from '../../components/Layout/config/SidebarItems';
import type { MenuItem as SidebarMenuItem } from '../../components/Layout/Sidebar';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useAccountVesselScope } from '../../hooks/useAccountVesselScope';
import { cn } from '../../lib/utils';
import { useModuleEntitlements } from '../admin/module-management';
import {
  buildSidebarPlacements,
  normalizeReportSidebarPlacements,
  normalizeSidebarPlacements,
  type EFormSidebarPlacement,
} from './sidebarPlacements';

function menuNodeAnchorPaths(node: SidebarMenuItem): string[] {
  const paths: string[] = [];
  if (typeof node.path === 'string' && node.path.length) paths.push(node.path);
  if (node.views?.length) {
    for (const v of node.views) {
      if (typeof v.path === 'string' && v.path.length) paths.push(v.path);
    }
  }
  return paths;
}

function menuNodeContainsAnchor(node: SidebarMenuItem, anchorPath: string | null | undefined): boolean {
  if (!anchorPath) return false;
  if (menuNodeAnchorPaths(node).includes(anchorPath)) return true;
  return (node.children ?? []).some((c) => menuNodeContainsAnchor(c, anchorPath));
}

export type EFormSidebarPlacementControlsProps = {
  placements: unknown;
  onChange: (next: EFormSidebarPlacement[]) => void;
  itemName: string;
  /** Defaults to form-style null → catalog_only. Reports should pass true. */
  defaultHidden?: boolean;
  dragToken?: string;
  /** Start expanded (default collapsed). */
  defaultExpanded?: boolean;
  /**
   * `button` — compact action that opens the configure modal.
   * `toolbar` — matches header action buttons; expands a panel below.
   * `card` — bordered accordion (save dialog / settings).
   */
  variant?: 'button' | 'toolbar' | 'card';
};

/** Compact card + Configure modal — same UX as Form builder Sidebar placement. */
export const EFormSidebarPlacementControls: React.FC<EFormSidebarPlacementControlsProps> = ({
  placements,
  onChange,
  itemName,
  defaultHidden = false,
  dragToken = 'eform-shortcut',
  defaultExpanded = false,
  variant = 'card',
}) => {
  const { colors } = useTheme();
  const normalize = defaultHidden ? normalizeReportSidebarPlacements : normalizeSidebarPlacements;
  const normalizedPlacements = normalize(placements);
  const shortcutPlacements = normalizedPlacements.filter(
    (p): p is Extract<EFormSidebarPlacement, { mode: 'shortcut' }> => p.mode === 'shortcut',
  );
  const catalogOnly = normalizedPlacements.some((p) => p.mode === 'catalog_only');
  const [expanded, setExpanded] = useState(defaultExpanded);
  const [configureOpen, setConfigureOpen] = useState(false);

  const { user, can, permissionsLoaded } = useAuth();
  const { entitlements } = useModuleEntitlements();
  const vesselAccountId = useAccountVesselScope();
  const canView = useMemo(() => {
    if (!(permissionsLoaded || user?.role === 'ADMIN')) return undefined;
    return (moduleKey: string) => can(moduleKey, 'view');
  }, [can, permissionsLoaded, user?.role]);
  const pathLabels = useMemo(
    () => collectSidebarPathLabels(getSidebarItems(canView, vesselAccountId, entitlements)),
    [canView, vesselAccountId, entitlements],
  );

  const summary =
    catalogOnly && !shortcutPlacements.length
      ? 'E-Forms'
      : !catalogOnly && !shortcutPlacements.length
        ? 'Hidden'
        : shortcutPlacements.length
          ? `${shortcutPlacements.length} shortcut${shortcutPlacements.length === 1 ? '' : 's'}`
          : '—';

  const placed = catalogOnly || shortcutPlacements.length > 0;

  const body = (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className={cn('text-xs', colors.textMuted)}>
          Pin this report in the sidebar, or place a shortcut next to another module.
        </p>
        <Button type="button" variant="secondary" size="sm" onClick={() => setConfigureOpen(true)}>
          <CogIcon className="h-4 w-4" />
          Configure
        </Button>
      </div>

      <div className="flex items-center gap-2">
        <Switch
          checked={catalogOnly}
          onChange={(e) => onChange(buildSidebarPlacements(shortcutPlacements, e.target.checked))}
        />
        <span className="inline-flex items-center gap-1.5 text-xs">
          <ViewColumnsIcon className={cn('h-3.5 w-3.5', colors.textMuted)} />
          Also show under E-Forms
        </span>
      </div>

      <div className="space-y-1.5">
        {catalogOnly && !shortcutPlacements.length ? (
          <div
            className={cn(
              'flex items-center gap-2 rounded-md border px-2.5 py-1.5 text-xs',
              colors.border,
              colors.background,
            )}
          >
            <DocumentTextIcon className={cn('h-3.5 w-3.5 shrink-0', colors.textMuted)} />
            Only in E-Forms catalog
          </div>
        ) : null}
        {!catalogOnly && !shortcutPlacements.length ? (
          <div
            className={cn(
              'flex items-center gap-2 rounded-md border px-2.5 py-1.5 text-xs',
              colors.border,
              colors.background,
            )}
          >
            <EyeIcon className={cn('h-3.5 w-3.5 shrink-0', colors.textMuted)} />
            Hidden from sidebar
          </div>
        ) : null}
        {shortcutPlacements.map((p, idx) => (
          <div
            key={`${p.anchorPath}-${p.insert}-${idx}`}
            className={cn(
              'rounded-md border px-2.5 py-1.5 text-xs flex items-start justify-between gap-2',
              colors.border,
              colors.background,
            )}
            title={p.anchorPath}
          >
            <div className="flex min-w-0 items-start gap-2">
              <MapPinIcon className={cn('mt-0.5 h-3.5 w-3.5 shrink-0', colors.textMuted)} />
              <div className="min-w-0">
                <div className="font-medium truncate">{itemName || 'Report'}</div>
                <div className={cn('mt-0.5 truncate', colors.textMuted)}>
                  {p.insert === 'before' ? 'Before' : 'After'} ·{' '}
                  {pathLabels[p.anchorPath] ?? p.anchorPath}
                </div>
              </div>
            </div>
            {catalogOnly ? (
              <span
                className={cn(
                  'shrink-0 rounded px-1.5 py-0.5 text-[10px] uppercase tracking-wide',
                  colors.textMuted,
                )}
              >
                +catalog
              </span>
            ) : null}
          </div>
        ))}
      </div>
    </div>
  );

  const modal = (
    <SidebarPlacementEditorModal
      isOpen={configureOpen}
      onClose={() => setConfigureOpen(false)}
      placements={normalizedPlacements}
      itemName={itemName}
      dragToken={dragToken}
      onChange={onChange}
    />
  );

  if (variant === 'button') {
    return (
      <>
        <Button
          type="button"
          size="sm"
          variant="secondary"
          onClick={() => setConfigureOpen(true)}
        >
          <ViewColumnsIcon className="h-4 w-4" />
          Menu
          <span
            className={cn(
              'rounded-full px-1.5 py-0.5 text-[10px] font-medium',
              placed
                ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950/50 dark:text-emerald-200'
                : 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300',
            )}
          >
            {summary}
          </span>
        </Button>
        {modal}
      </>
    );
  }

  if (variant === 'toolbar') {
    return (
      <>
        <Button
          type="button"
          size="sm"
          variant="secondary"
          className="h-10 min-w-[132px] justify-between gap-2 rounded-xl"
          onClick={() => setExpanded((v) => !v)}
          aria-expanded={expanded}
        >
          <span className="flex items-center gap-1.5 truncate">
            <ViewColumnsIcon className="h-4 w-4 shrink-0" />
            <span className="truncate">Sidebar</span>
          </span>
          <span className="flex items-center gap-1">
            <span
              className={cn(
                'max-w-[6.5rem] truncate rounded-full px-1.5 py-0.5 text-[10px] font-medium',
                placed
                  ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950/50 dark:text-emerald-200'
                  : 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300',
              )}
            >
              {summary}
            </span>
            <ChevronDownIcon
              className={cn('h-4 w-4 transition-transform', expanded && 'rotate-180')}
            />
          </span>
        </Button>
        {expanded ? (
          <div
            className={cn(
              'basis-full w-full rounded-lg border px-3 py-2.5',
              colors.border,
              'bg-slate-50/70 dark:bg-slate-950/40',
            )}
          >
            {body}
          </div>
        ) : null}
        {modal}
      </>
    );
  }

  return (
    <div className={cn('rounded-lg border overflow-hidden', colors.border)}>
      <div
        className={cn(
          'flex items-center gap-2 px-2 py-1.5',
          expanded && 'border-b',
          colors.border,
          'bg-slate-50/80 dark:bg-slate-900/40',
        )}
      >
        <button
          type="button"
          className={cn(
            'flex min-w-0 flex-1 items-center gap-2 rounded-md px-1 py-1 text-left transition-colors',
            'hover:bg-slate-200/60 dark:hover:bg-slate-800',
          )}
          onClick={() => setExpanded((v) => !v)}
          aria-expanded={expanded}
        >
          {expanded ? (
            <ChevronDownIcon className={cn('h-4 w-4 shrink-0', colors.textMuted)} />
          ) : (
            <ChevronRightIcon className={cn('h-4 w-4 shrink-0', colors.textMuted)} />
          )}
          <span className="min-w-0 flex-1 truncate text-sm font-medium">Sidebar placement</span>
          {!expanded ? (
            <span className={cn('shrink-0 truncate text-xs', colors.textMuted)}>{summary}</span>
          ) : null}
        </button>
      </div>

      {expanded ? <div className="p-2">{body}</div> : null}
      {modal}
    </div>
  );
};

function SidebarPlacementEditorModal({
  isOpen,
  onClose,
  placements,
  itemName,
  dragToken,
  onChange,
}: {
  isOpen: boolean;
  onClose: () => void;
  placements: EFormSidebarPlacement[];
  itemName: string;
  dragToken: string;
  onChange: (next: EFormSidebarPlacement[]) => void;
}) {
  const { colors } = useTheme();
  const keepInCatalog = placements.some((p) => p.mode === 'catalog_only');
  const shortcutPlacements = placements.filter(
    (p): p is Extract<EFormSidebarPlacement, { mode: 'shortcut' }> => p.mode === 'shortcut',
  );
  const primaryShortcut = shortcutPlacements[0] ?? null;

  const { user, can, permissionsLoaded } = useAuth();
  const { entitlements } = useModuleEntitlements();
  const vesselAccountId = useAccountVesselScope();

  const canView = useMemo(() => {
    if (!(permissionsLoaded || user?.role === 'ADMIN')) return undefined;
    return (moduleKey: string) => can(moduleKey, 'view');
  }, [can, permissionsLoaded, user?.role]);

  const sidebarTree = useMemo(
    () => getSidebarItems(canView, vesselAccountId, entitlements),
    [canView, vesselAccountId, entitlements],
  );

  const pathLabels = useMemo(() => collectSidebarPathLabels(sidebarTree), [sidebarTree]);
  const [hoverTarget, setHoverTarget] = useState<{
    anchorPath: string;
    insert: 'before' | 'after';
  } | null>(null);
  const [expandedKeys, setExpandedKeys] = useState<Record<string, boolean>>({});

  useEffect(() => {
    if (!isOpen) {
      setHoverTarget(null);
      setExpandedKeys({});
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || !primaryShortcut?.anchorPath) return;
    setExpandedKeys((prev) => {
      const next = { ...prev };
      const walk = (nodes: SidebarMenuItem[], parentKey = '') => {
        nodes.forEach((node, idx) => {
          if (node.sectionLabel) return;
          const key = `${parentKey}/${node.name}-${idx}`;
          if ((node.children?.length ?? 0) > 0) {
            if (menuNodeContainsAnchor(node, primaryShortcut.anchorPath)) {
              next[key] = true;
            }
            walk(node.children ?? [], key);
          }
        });
      };
      walk(sidebarTree);
      return next;
    });
  }, [isOpen, primaryShortcut?.anchorPath, sidebarTree]);

  const writePlacements = (
    nextShortcuts: Extract<EFormSidebarPlacement, { mode: 'shortcut' }>[],
    catalog: boolean,
  ) => {
    onChange(buildSidebarPlacements(nextShortcuts, catalog));
  };

  const applyCatalogOnly = () => {
    setHoverTarget(null);
    writePlacements([], true);
  };

  const applyShortcutPlacement = (anchorPath: string, insert: 'before' | 'after') => {
    setHoverTarget(null);
    writePlacements(
      [{ mode: 'shortcut', anchorPath, insert, sortOrder: Date.now() }],
      keepInCatalog || !shortcutPlacements.length,
    );
  };

  const removeShortcuts = () => {
    setHoverTarget(null);
    writePlacements([], keepInCatalog);
  };

  const toggleExpanded = (key: string) => {
    setExpandedKeys((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const ShortcutPreviewRow = ({ level, insert }: { level: number; insert: 'before' | 'after' }) => (
    <div
      className={cn(
        'mx-1 my-0.5 flex items-center gap-2 rounded-md border border-dashed px-2 py-1.5 text-sm',
        'border-sky-400/70 bg-sky-50 text-sky-900 dark:border-sky-500/50 dark:bg-sky-950/40 dark:text-sky-100',
      )}
      style={{ marginLeft: `${8 + level * 12}px` }}
    >
      <span className="rounded bg-sky-600/15 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-sky-700 dark:text-sky-200">
        {insert}
      </span>
      <DocumentTextIcon className="w-4 h-4 shrink-0 opacity-80" />
      <span className="min-w-0 truncate font-medium">{itemName || 'Report'}</span>
      <span className="ml-auto shrink-0 text-[10px] uppercase tracking-wide opacity-70">Shortcut</span>
    </div>
  );

  const SidebarNode: React.FC<{
    node: SidebarMenuItem;
    level: number;
    nodeKey: string;
  }> = ({ node, level, nodeKey }) => {
    const paths = menuNodeAnchorPaths(node);
    const anchorPath = paths[0];
    const hasAnchor = Boolean(anchorPath);
    const hasChildren = (node.children?.length ?? 0) > 0;
    const indent = 8 + level * 12;
    const isExpanded = hasChildren ? expandedKeys[nodeKey] === true : false;

    const placedHere =
      hasAnchor && primaryShortcut && paths.includes(primaryShortcut.anchorPath)
        ? primaryShortcut
        : null;
    const hoveringHere =
      hasAnchor && hoverTarget && paths.includes(hoverTarget.anchorPath) ? hoverTarget : null;
    const previewInsert = hoveringHere?.insert ?? placedHere?.insert ?? null;
    const isAnchor = Boolean(placedHere);

    if (node.sectionLabel) {
      return (
        <div className={cn('px-2 py-2 text-[10px] uppercase tracking-[0.2em] font-semibold', colors.textMuted)}>
          {node.sectionLabel}
        </div>
      );
    }

    const rowContent = (
      <div className="flex items-center gap-2 min-w-0 w-full">
        {hasChildren ? (
          <button
            type="button"
            className={cn(
              'shrink-0 rounded p-0.5 transition-colors',
              colors.textMuted,
              'hover:bg-slate-200/70 dark:hover:bg-slate-700',
            )}
            title={isExpanded ? 'Collapse' : 'Expand'}
            onClick={(e) => {
              e.stopPropagation();
              toggleExpanded(nodeKey);
            }}
          >
            {isExpanded ? (
              <ChevronDownIcon className="w-3.5 h-3.5" />
            ) : (
              <ChevronRightIcon className="w-3.5 h-3.5" />
            )}
          </button>
        ) : (
          <span className="w-4 shrink-0" />
        )}
        {node.icon ? (
          <span className={cn('shrink-0 flex items-center justify-center w-4 h-4', colors.textMuted)}>
            {node.icon}
          </span>
        ) : (
          <span className="w-4 shrink-0" />
        )}
        <span className="truncate flex-1 text-left">{node.name}</span>
        {isAnchor ? (
          <span className="rounded bg-sky-600/15 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-sky-700 dark:text-sky-200">
            Anchor
          </span>
        ) : null}
      </div>
    );

    return (
      <div>
        {previewInsert === 'before' ? <ShortcutPreviewRow level={level} insert="before" /> : null}
        <div
          className={cn(
            'mx-1 rounded-md px-2 py-1.5 text-sm select-none transition-colors',
            hasAnchor ? 'cursor-copy' : hasChildren ? 'cursor-pointer' : 'opacity-80',
            isAnchor
              ? 'bg-sky-100/80 ring-1 ring-sky-400/60 dark:bg-sky-950/50 dark:ring-sky-500/40'
              : hoveringHere
                ? 'bg-slate-100 dark:bg-slate-800'
                : 'hover:bg-slate-50 dark:hover:bg-slate-800/60',
          )}
          style={{ paddingLeft: `${indent}px` }}
          title={
            hasAnchor
              ? `Drop to place near ${pathLabels[anchorPath!] ?? anchorPath}`
              : hasChildren
                ? isExpanded
                  ? 'Collapse'
                  : 'Expand'
                : undefined
          }
          onClick={hasChildren && !hasAnchor ? () => toggleExpanded(nodeKey) : undefined}
          onDragOver={
            hasAnchor
              ? (e) => {
                  if (!e.dataTransfer.types?.includes('text/plain')) return;
                  e.preventDefault();
                  const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
                  const insert: 'before' | 'after' =
                    e.clientY < rect.top + rect.height / 2 ? 'before' : 'after';
                  if (
                    !hoverTarget ||
                    hoverTarget.anchorPath !== anchorPath ||
                    hoverTarget.insert !== insert
                  ) {
                    setHoverTarget({ anchorPath: anchorPath!, insert });
                  }
                }
              : undefined
          }
          onDragLeave={
            hasAnchor
              ? () => {
                  setHoverTarget((prev) => (prev?.anchorPath === anchorPath ? null : prev));
                }
              : undefined
          }
          onDrop={
            hasAnchor
              ? (e) => {
                  e.preventDefault();
                  const token = e.dataTransfer.getData('text/plain');
                  if (token !== dragToken) return;
                  const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
                  const insert: 'before' | 'after' =
                    e.clientY < rect.top + rect.height / 2 ? 'before' : 'after';
                  applyShortcutPlacement(anchorPath!, insert);
                }
              : undefined
          }
        >
          {rowContent}
        </div>
        {previewInsert === 'after' ? <ShortcutPreviewRow level={level} insert="after" /> : null}
        {hasChildren && isExpanded
          ? node.children!.map((c, idx) => (
              <SidebarNode
                key={`${c.name}-${idx}-${c.path ?? c.views?.[0]?.path ?? ''}`}
                node={c}
                level={level + 1}
                nodeKey={`${nodeKey}/${c.name}-${idx}`}
              />
            ))
          : null}
      </div>
    );
  };

  const placementSummary = primaryShortcut
    ? `${primaryShortcut.insert === 'before' ? 'Before' : 'After'} · ${
        pathLabels[primaryShortcut.anchorPath] ?? primaryShortcut.anchorPath
      }`
    : keepInCatalog
      ? 'Only in E-Forms catalog'
      : 'Hidden from sidebar';

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl">
      <ModalHeader>
        <ModalTitle>Sidebar placement · {itemName || 'Report'}</ModalTitle>
      </ModalHeader>
      <ModalContent className="space-y-4">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
          <div className="lg:col-span-4 space-y-3">
            <div className={cn('rounded-lg border p-3 space-y-3', colors.border)}>
              <div>
                <p className="text-sm font-medium">Drag the shortcut</p>
                <p className={`text-xs mt-1 ${colors.textMuted}`}>
                  Expand a module, then drop on a menu row. Top half = before, bottom half = after.
                </p>
              </div>

              <div
                draggable
                onDragStart={(e) => {
                  e.dataTransfer.setData('text/plain', dragToken);
                  e.dataTransfer.effectAllowed = 'copy';
                }}
                className={cn(
                  'inline-flex w-full items-center gap-2 rounded-lg border px-3 py-2.5 cursor-grab select-none',
                  'bg-sky-50 border-sky-300 text-sky-900 dark:bg-sky-950/40 dark:border-sky-700 dark:text-sky-100',
                )}
              >
                <GripVerticalIcon className="w-4 h-4 shrink-0 opacity-70" />
                <DocumentTextIcon className="w-4 h-4 shrink-0 opacity-80" />
                <span className="flex-1 min-w-0 text-sm font-medium truncate">
                  {itemName || 'Report'}
                </span>
                <Badge variant="secondary">{primaryShortcut ? 'Placed' : 'Not placed'}</Badge>
              </div>

              <div className={cn('rounded-lg border px-3 py-2 space-y-1', colors.border, colors.background)}>
                <p className={`text-[10px] uppercase tracking-wide ${colors.textMuted}`}>Current placement</p>
                <p className="text-sm font-medium">{itemName || 'Report'}</p>
                <p className={`text-xs ${colors.textMuted}`}>{placementSummary}</p>
                {keepInCatalog && primaryShortcut ? (
                  <p className={`text-xs ${colors.textMuted}`}>Also kept in E-Forms catalog</p>
                ) : null}
              </div>

              <div className="space-y-2">
                <button
                  type="button"
                  className={cn(
                    'w-full rounded-lg border px-3 py-2 text-left text-sm transition-colors',
                    !primaryShortcut && keepInCatalog
                      ? 'border-sky-400 bg-sky-50 dark:bg-sky-950/40'
                      : colors.border,
                  )}
                  onClick={applyCatalogOnly}
                  onDragOver={(e) => {
                    if (e.dataTransfer.types?.includes('text/plain')) e.preventDefault();
                  }}
                  onDrop={(e) => {
                    e.preventDefault();
                    if (e.dataTransfer.getData('text/plain') !== dragToken) return;
                    applyCatalogOnly();
                  }}
                >
                  E-Forms catalog only
                </button>

                <div className="flex items-start gap-2 px-1">
                  <Switch
                    checked={keepInCatalog}
                    onChange={(e) => writePlacements(shortcutPlacements, e.target.checked)}
                  />
                  <div className="text-xs">
                    Also show under E-Forms
                    {primaryShortcut ? (
                      <p className={cn('mt-0.5', colors.textMuted)}>
                        Turn off to remove this report from the E-Forms sidebar group.
                      </p>
                    ) : null}
                  </div>
                </div>

                {primaryShortcut ? (
                  <button
                    type="button"
                    className={cn('w-full rounded-lg border px-3 py-2 text-sm', colors.border, colors.textMuted)}
                    onClick={removeShortcuts}
                  >
                    Clear module shortcut
                  </button>
                ) : null}
              </div>
            </div>
          </div>

          <div
            className={cn(
              'lg:col-span-8 min-h-[28rem] max-h-[55vh] overflow-y-auto rounded-lg border p-2',
              colors.border,
            )}
          >
            <div className="sticky top-0 z-10 mb-1 flex items-center justify-between gap-2 rounded-md px-2 py-2 backdrop-blur-sm bg-white/80 dark:bg-slate-950/70">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide">Sidebar preview</p>
                <p className={`text-[11px] ${colors.textMuted}`}>
                  Expand modules · blue row = your shortcut
                </p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  className={cn('text-[11px] underline-offset-2 hover:underline', colors.textMuted)}
                  onClick={() => {
                    const next: Record<string, boolean> = {};
                    const walk = (nodes: SidebarMenuItem[], parentKey = '') => {
                      nodes.forEach((node, idx) => {
                        if (node.sectionLabel) return;
                        const key = `${parentKey}/${node.name}-${idx}`;
                        if ((node.children?.length ?? 0) > 0) {
                          next[key] = true;
                          walk(node.children ?? [], key);
                        }
                      });
                    };
                    walk(sidebarTree);
                    setExpandedKeys(next);
                  }}
                >
                  Expand all
                </button>
                <button
                  type="button"
                  className={cn('text-[11px] underline-offset-2 hover:underline', colors.textMuted)}
                  onClick={() => setExpandedKeys({})}
                >
                  Collapse all
                </button>
                {primaryShortcut ? (
                  <Badge variant="success">Placed</Badge>
                ) : (
                  <Badge variant="secondary">Not placed</Badge>
                )}
              </div>
            </div>
            <div className="space-y-0.5 pb-2">
              {sidebarTree.map((root, idx) => (
                <SidebarNode
                  key={`${root.name}-${idx}-${root.path ?? ''}`}
                  node={root}
                  level={0}
                  nodeKey={`/${root.name}-${idx}`}
                />
              ))}
            </div>
          </div>
        </div>
      </ModalContent>
      <ModalFooter>
        <Button variant="secondary" onClick={onClose}>
          Done
        </Button>
      </ModalFooter>
    </Modal>
  );
};
